var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useLocale } from "@aviv-unified-frontend/runtime/i18n";
import { useState } from "react";
import { fetchSimilarListings } from "@/api/fetchSimilarListings";
import { useDropdownItems } from "@/components/contacting/ContactForm/EnrichedContactFormFields/useDropdownItems";
import { useIsConfirmationPageDisabled } from "@/components/contacting/hooks/useIsConfirmationPageDisabled";
import { useIsEnrichedContactFormFields } from "@/components/contacting/hooks/useIsEnrichedContactFormFields";
import { SIMILAR_LISTING_PATH } from "@/constants/api";
import { useConfig, useCrsBaseUrl } from "@/contexts/cdpConfig";
import { useClassified, useLegacyId } from "@/contexts/classified";
import { useConfirmationPage } from "@/contexts/confirmPage";
import { useCdpId, useContactBrand, useContactContext, } from "@/contexts/contact";
import { useBrandUrl, useCustomerAction, useSendLeadTracking } from "@/hooks";
import { useClassifiedAnalytics } from "@/hooks/tracking/classifiedAnalytics";
import { useTrackingLegacyLeadEvents } from "@/hooks/tracking/trackingLegacy";
import { useI18n } from "@/shared/hooks";
import { CA_EVENT_TYPES } from "@/types/classifiedAnalytics";
import { buildSendMailActionParams, sendLead } from "@/utils";
import { useSnackbar } from "@gemini/ui";
import { useAuthentication } from "@aviv-unified-frontend/runtime/authentication";
export const useSendLead = () => {
    var _a, _b, _c, _d;
    const { t } = useI18n();
    const { language } = useLocale();
    const { add: addSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(false);
    const [postLeadError, setPostLeadError] = useState(false);
    const cdpLegacyId = useLegacyId();
    const classifiedId = useCdpId();
    const brand = useContactBrand();
    const { unsafe_accessToken } = useAuthentication();
    const { customerActionExposeContact } = useCustomerAction();
    const { sendClassifiedAnalyticsEvent } = useClassifiedAnalytics();
    const { triggerConfirmView } = useConfirmationPage();
    const distributionType = (_b = (_a = useClassified()) === null || _a === void 0 ? void 0 : _a.rawData) === null || _b === void 0 ? void 0 : _b.distributionType;
    const isRental = distributionType === "RENT";
    const isEnrichedRentalContactLeads = useIsEnrichedContactFormFields();
    const isConfirmationPageDisabled = useIsConfirmationPageDisabled();
    const baseUrl = useCrsBaseUrl();
    const sendLeadUrl = `${baseUrl}/contacting`;
    const similarListingsUrl = useBrandUrl({
        endpoint: SIMILAR_LISTING_PATH(classifiedId),
    });
    const featuresFlags = (_c = useConfig()) === null || _c === void 0 ? void 0 : _c.featuresFlags;
    const { legacyLeadConversionMail, legacyLeadConfirmationMail } = useTrackingLegacyLeadEvents(); //legacy GA3 for harvest
    const legacyTrackingData = (_d = useClassified()) === null || _d === void 0 ? void 0 : _d.legacyTracking;
    const hasLegacyTrackingData = !!legacyTrackingData;
    const { leadFormOk, leadFormConfirm, getAdditionalProps } = useSendLeadTracking();
    const { contactTracking, toggleMultiInquiryModal, prefetchSimilarListings } = useContactContext();
    const householdTypeItems = useDropdownItems("contact.form.household_type_items", 4);
    const netMonthlyIncome = useDropdownItems("contact.form.net_monthly_income", 9);
    const preferredMoveInDate = useDropdownItems("contact.form.preferred_move_in_date_items", 5);
    const workStatus = useDropdownItems("contact.form.work_status_items", 10);
    const handleSubmit = (data, av_zone) => __awaiter(void 0, void 0, void 0, function* () {
        const trackingProps = getAdditionalProps(data, av_zone);
        !isRental &&
            prefetchSimilarListings({
                id: classifiedId,
                url: similarListingsUrl,
                fetcher: fetchSimilarListings,
            });
        const getEnrichedFieldLabel = (items, key) => {
            var _a;
            return key && items ? (_a = items.find((item) => item.id === key)) === null || _a === void 0 ? void 0 : _a.label : "";
        };
        // Enrich message property with extra metadata
        if (isEnrichedRentalContactLeads) {
            data.message = `${data.message}   • Haushaltsgröße: ${getEnrichedFieldLabel(householdTypeItems, data.householdType)} • Beschäftigungsstatus: ${getEnrichedFieldLabel(workStatus, data.workStatus)} • Haushalts-Nettoeinkommen: ${getEnrichedFieldLabel(netMonthlyIncome, data.netMonthlyIncome)} • Gewünschtes Einzugsdatum: ${getEnrichedFieldLabel(preferredMoveInDate, data.preferredMoveInDate)} • `;
        }
        if (hasLegacyTrackingData) {
            legacyLeadConversionMail({
                eventData: {
                    infos: contactTracking === null || contactTracking === void 0 ? void 0 : contactTracking.trackingLegacyInfos,
                    email_lead: data === null || data === void 0 ? void 0 : data.email,
                },
            }); //legacy GA3 for harvest
        }
        leadFormOk(trackingProps);
        const sendLeadDatas = Object.assign(Object.assign({}, data), { language, classifiedId: contactTracking.unitId || classifiedId, gokId: cdpLegacyId, brand,
            featuresFlags });
        try {
            setLoading(true);
            const response = yield sendLead(sendLeadUrl, sendLeadDatas, unsafe_accessToken);
            if (response.status !== 200) {
                throw new Error("Error sending lead");
            }
            leadFormConfirm(Object.assign(Object.assign({}, trackingProps), { eventData: {
                    email_lead: data === null || data === void 0 ? void 0 : data.email,
                } }));
            if (hasLegacyTrackingData) {
                legacyLeadConfirmationMail({
                    eventData: {
                        infos: contactTracking === null || contactTracking === void 0 ? void 0 : contactTracking.trackingLegacyInfos,
                        email_lead: data === null || data === void 0 ? void 0 : data.email,
                    },
                });
            }
            sendClassifiedAnalyticsEvent(CA_EVENT_TYPES.CONTACT_FORM_SUBMIT);
            yield customerActionExposeContact(buildSendMailActionParams(data));
            setLoading(false);
            if (isRental) {
                isConfirmationPageDisabled
                    ? addSnackbar({
                        variant: "info",
                        description: t("cdpSeeker_contactForm_success_message"),
                    })
                    : triggerConfirmView("form");
            }
            else {
                toggleMultiInquiryModal();
            }
        }
        catch (error) {
            setLoading(false);
            setPostLeadError(true);
        }
    });
    return { handleSubmit, loading, postLeadError };
};
